import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plug, CreditCard, KeyRound, Users, ScrollText, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import SettingsConnectors from "@/components/settings/SettingsConnectors";
import SettingsPlan from "@/components/settings/SettingsPlan";
import SettingsApiKeys from "@/components/settings/SettingsApiKeys";
import SettingsMembers from "@/components/settings/SettingsMembers";
import SettingsAudit from "@/components/settings/SettingsAudit";
import SettingsPolicy from "@/components/settings/SettingsPolicy";

/**
 * Everything a workspace can configure about itself.
 *
 * Each tab names the permission it needs rather than a role, for the same
 * reason the backend does: the mapping from role to permission is allowed to
 * change, and a UI that hardcodes "admin" would silently disagree with it.
 *
 * Tabs the current user cannot use are not rendered at all. That differs from
 * the connector kind picker, which shows locked kinds — deliberately. A locked
 * kind is something you can buy; a permission you lack is something only a
 * colleague can grant, so advertising it is noise rather than an offer.
 */
const TABS = [
  { id: "connectors", label: "Connectors", icon: Plug,
    permission: "connector.manage", render: () => <SettingsConnectors /> },
  { id: "plan", label: "Plan & usage", icon: CreditCard,
    permission: null, render: () => <SettingsPlan /> },
  { id: "keys", label: "API keys", icon: KeyRound,
    permission: "member.manage", render: () => <SettingsApiKeys /> },
  { id: "members", label: "People", icon: Users,
    permission: "member.manage", render: () => <SettingsMembers /> },
  { id: "policy", label: "Policy", icon: ShieldCheck,
    permission: "policy.manage", render: () => <SettingsPolicy /> },
  { id: "audit", label: "Audit", icon: ScrollText,
    permission: "audit.read", render: () => <SettingsAudit /> },
];

export default function Settings() {
  const { permissions } = useAuth();
  const visible = TABS.filter(
    (t) => !t.permission || permissions.includes(t.permission)
  );
  // ?tab=plan lets a banner elsewhere send someone straight to a tab.
  const [params] = useSearchParams();
  const asked = visible.find((t) => t.id === params.get("tab"))?.id;
  const [active, setActive] = useState(asked || visible[0]?.id || "plan");
  const current = visible.find((t) => t.id === active) || visible[0];

  return (
    <div className="px-4 py-5 md:px-8 md:py-6">
      <div className="text-[11px] uppercase tracking-[0.18em] mb-1"
           style={{ color: "var(--text-muted)" }}>
        Workspace
      </div>
      <h1 className="font-display text-[26px]">Settings</h1>
      <p className="text-[13px] mt-1" style={{ color: "var(--text-secondary)" }}>
        Sources, people and keys for this workspace. Everything here is scoped to
        it — nothing you change affects another.
      </p>

      <div className="flex gap-1 mt-5 mb-5 border-b scroll-strip" style={{ borderColor: "var(--border)" }}>
        {visible.map((t) => (
          <button
            key={t.id}
            onClick={() => setActive(t.id)}
            data-testid={`settings-tab-${t.id}`}
            className="flex items-center gap-2 px-3 py-2 text-[13px] -mb-px whitespace-nowrap shrink-0"
            style={{
              color: active === t.id ? "var(--text)" : "var(--text-secondary)",
              borderBottom: `2px solid ${active === t.id ? "var(--text)" : "transparent"}`,
            }}
          >
            <t.icon size={14} strokeWidth={1.8} />
            {t.label}
          </button>
        ))}
      </div>

      <div data-testid={`settings-panel-${current?.id}`}>{current?.render()}</div>
    </div>
  );
}
